import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, Subscription } from "rxjs/Rx";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { debounceTime } from "rxjs/operator/debounceTime";
import { PropertyService } from "../shared/property/property.service";
import { LoaderService } from "../shared/services/loader.service";
import { Property } from "../shared/property/property";
import { FileUploadService } from "../shared/services/fileuploadservice";
import { PapaParseService } from "ngx-papaparse";
import { CommunityService } from "../shared/services/community/community.service";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "../shared/services/auth.service";
import { BackendService } from "../shared/services/backend.service";

@Component({
  selector: "app-property-management",
  templateUrl: "./property-management.component.html",
  styleUrls: ["./property-management.component.css"]
})
export class PropertyManagementComponent implements OnInit, OnDestroy {
  activeRouteIndex: number;
  community$: any;
  communityId: string;
  community: any;
  properties;
  property;
  address: string;
  name: string;
  type: string;
  query;
  fileName;
  fileSize;
  confirmationMessage = "";
  confirmationTitle = "";
  modalReference: NgbModalRef;
  successMessage = "";
  errorMessage = "";
  modalSuccessMessage = "";
  modalErrorMessage = "";
  canUpload = false;
  file;
  fileUploadErrorMessage;
  propertyTypes = ["Commercial", "Land", "Industrial", "Residential"];
  private _alert: Subject<string> = new Subject<string>();
  private alertSubscription: Subscription = new Subscription();
  sampleFileUrl = "https://bit.ly/2KHncDp";
  actionType: string;
  operatorId: string;
  isLoading: boolean;

  constructor(
    private csvParseService: PapaParseService,
    private modalService: NgbModal,
    private authService: AuthService,
    private communityService: CommunityService,
    private loaderService: LoaderService,
    private propertyService: PropertyService,
    private uploadService: FileUploadService,
    private route: ActivatedRoute
  ) { this.isLoading = false; }

  ngOnInit(): void {
    this.query = "";
    this.isLoading = this.loaderService.startLoader();
    this.operatorId = BackendService.generateUserId(this.authService.getAuthState().email);
    this.route.params.subscribe(params => {
      this.activeRouteIndex = params.index;
      this.communityId = params.communityId;
      this.community$ = this.communityService.getCommunityById(
        this.communityId
      );
      this.propertyService.getProperties(this.communityId)
        .subscribe(properties => {
          properties.sort((a, b) => {
            const nameA = a.address.toUpperCase(); // ignore upper and lowercase
            const nameB = b.address.toUpperCase(); // ignore upper and lowercase
            const result = nameA < nameB ? -1 : nameA > nameB ? 1 : 0;

            return result;
          });
          this.properties = properties;
          this.properties.forEach(property => {

            const unitWatcher = this.propertyService
              .checkIfUnitsExist(property.id)
              .subscribe(units => {
                property.units = units.length;
                if (property.units > 0) {
                  unitWatcher.unsubscribe();
                }
              });
          });
          this.isLoading = this.loaderService.stopLoader();
        });
    });
  }

  ngOnDestroy(): void {
    this.closeModal();
  }

  openBulkUploadModal(modal): void {
    this.openModal(modal, "md");
  }

  openFileUploader(): void {
    const element = document.getElementById("bulkUploader") as HTMLElement;
    element.click();
  }

  clearFileUpload(): void {
    this.fileName = "";
    this.fileSize = "";
    this.canUpload = false;
  }

  getFileUpload(event): void {
    const result = this.uploadService.verifyFileIsCSV(event);
    if (result.file) {
      this.fileName = result.file.name;
      this.fileSize = `${result.file.size}B`;
      this.file = result.file;
      this.canUpload = true;
    } else {
      this.fileUploadErrorMessage = result.message;
    }
  }

  createOrEditProperty(modal, property: any): void {
    if (property) {
      this.property = property;
      this.name = this.property.name;
      this.address = this.property.address;
      this.type = this.property.type;
    } else {
      this.property = new Property();
      this.name = this.address = this.type = "";
    }
    this.openModal(modal, "md");
  }

  saveProperty(): void {
    this.submit();
    this.closeModal();
  }

  saveAndAddProperty(): void {
    this.submit();
  }

  confirmAction(type, property: any, modal): void {
    this.property = property;
    this.actionType = type;
    const message = `Are you sure you want to ${type} "${property.name}"`;
    this.confirm("Attention!", message, modal);
  }

  deleteProperty(): void {
    this.propertyService.delete(this.communityId, this.property.id);
    this.closeModal();
    this.showAlert(true, "Property deleted successfully.", false);
  }

  activateProperty(): void {
    this.propertyService.activate(this.communityId, this.property.id);
    this.closeModal();
    this.actionType = "";
    this.showAlert(true, "Property activated successfully.", false);
  }

  deactivateProperty(): void {
    this.propertyService.deactivate(this.communityId, this.property.id);
    this.closeModal();
    this.actionType = "";
    this.showAlert(true, "Property deactivated successfully.", false);
  }

  submit(): any {
    const property = new Property("", this.name, this.address, this.type);
    if (this.property.id) {
      property["id"] = this.property.id;
      this.propertyService.update(this.communityId, this.property.id, property).then(
        status => {
          if (status) {
            this.showAlert(true, "Property updated successfully.", false);
            this.propertyService.getPropertyById(this.communityId, this.property.id).toPromise().then(
              updatedProperty => {
                this.property = updatedProperty;
              }
            );
          } else {
            this.showAlert(false,
              "Oops! unable to update property, no changes was made or duplicate property address encountered.", false);
          }
        }
      );
    } else {
      this.propertyService.store(this.communityId, property).then(
        status => {
          if (status) {
            this.showAlert(true, "Property added successfully.", false);
          } else {
            this.showAlert(false, "Oops! unable to add property, possible duplicate property names encountered.", false);
          }
        }
      );
    }
    this.name = this.address = this.type = "";
  }

  upload(): void {
    this.isLoading = this.loaderService.startLoader();
    this.csvParseService.parse(this.file, {
      complete: results => {
        this.propertyService.
          import(results.data, this.communityId, this.operatorId).
          then(response => {
            this.clearFileUpload();
            this.isLoading = this.loaderService.stopLoader();
            let message = response.uploadOccur ? "Properties uploaded successfully " : "No propert was uploaded";
            message += response.duplicates ? `, ${response.duplicates} duplicate(s) was igonred` : "";
            message += response.existingCount ? ` and ${response.existingCount} already exist in database.` : ".";
            this.showAlert(true, message, false);
          });
      },
      error: () => {
        this.isLoading = this.loaderService.stopLoader();
        this.showAlert(
          false,
          "Properties could not be uploaded. Please try again",
          false
        );
      }
    });
    this.closeModal();
  }

  confirm(title, message, modal): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.openModal(modal, "sm");
  }

  openModal(modal, modalSize): void {
    this.modalReference = this.modalService.open(modal, { size: modalSize });
  }

  closeModal(): void {
    if (this.modalReference) {
      this.modalReference.close();
    }
  }

  showAlert(msgType, msg, isModal): void {
    this.alertSubscription = this._alert.subscribe(message => {
      if (isModal) {
        msgType
          ? (this.modalSuccessMessage = message)
          : (this.modalErrorMessage = message);
      } else {
        msgType
          ? (this.successMessage = message)
          : (this.errorMessage = message);
      }
    });
    debounceTime.call(this._alert, 3000).subscribe(() => {
      if (isModal) {
        msgType
          ? (this.modalSuccessMessage = undefined)
          : (this.modalErrorMessage = undefined);
      } else {
        msgType
          ? (this.successMessage = undefined)
          : (this.errorMessage = undefined);
      }
    });
    this._alert.next(msg);
    this.alertSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/property-management/property-management.component.ts